import React, { useEffect, useState, useCallback } from 'react';
import { View, Text, StyleSheet, FlatList, ActivityIndicator, TouchableOpacity, Modal, TextInput, ScrollView } from 'react-native';
import { useLocalSearchParams } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';

import { api } from '../../src/api/client';
import { AdminToolbar } from '../../src/components/AdminList';
import ContentContainer from '../../src/components/ContentContainer';
import PageFooter from '../../src/components/PageFooter';
import InlineConfirm from '../../src/components/InlineConfirm';
import InlineMessage from '../../src/components/InlineMessage';
import { useTheme } from '../../src/context/ThemeContext';
import { useResponsive } from '../../src/utils/responsive';
import { spacing, fontSize, fontWeight, borderRadius } from '../../src/utils/theme';
import type { ColorTheme } from '../../src/utils/theme';
import { useModalBottomPadding } from '../../src/utils/useModalBottomPadding';
import { titleCase, formatPhone } from '../../src/utils/format';

interface FleetItem {
  id: string; companyName: string; status: string;
  billingEmail: string; businessName: string; createdAt: string;
  paymentTermsDays?: number; primaryContactName?: string; primaryContactEmail?: string;
  primaryContactPhone?: string; billingAddress?: string; billingCity?: string;
  billingState?: string; billingZip?: string; taxId?: string;
  pricingTierId?: string | null; ownerId?: string;
  invoiceAutoGeneration?: string | null;
  autoGenFrequency?: string | null;
  autoGenDayOfWeek?: number | null;
}

const STATUS_FILTERS = [
  { label: 'Active', value: 'active' },
  { label: 'Suspended', value: 'suspended' },
  { label: 'Cancelled', value: 'cancelled' },
];

const statusColor = (s: string) => {
  if (s === 'active') return '#059669';
  if (s === 'suspended') return '#d97706';
  return '#dc2626';
};

/** AdminFleetAccounts - list, search, and manage fleet customer accounts platform-wide. */
export default function AdminFleetAccounts() {
  const { colors } = useTheme();
  const { width, height } = useResponsive();
  const isMobile = width < 768;
  const styles = useStyles(colors, isMobile);
  const modalBottomPadding = useModalBottomPadding();
  const { ownerId: paramOwnerId, search: paramSearch } = useLocalSearchParams<{ ownerId?: string; search?: string }>();
  const [accounts, setAccounts] = useState<FleetItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [editItem, setEditItem] = useState<FleetItem | null>(null);
  const [editData, setEditData] = useState<any>(null);
  const [saving, setSaving] = useState(false);
  const [inlineMsg, setInlineMsg] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [tiers, setTiers] = useState<{ id: string; tierName: string }[]>([]);

  const handleDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await api(`/api/admin/fleet/accounts/${confirmDelete}`, { method: 'DELETE' });
      setConfirmDelete(null);
      setInlineMsg({ type: 'success', message: 'Fleet account deleted' });
      fetchData(page, search, statusFilter);
    } catch (err: any) { setInlineMsg({ type: 'error', message: err.message }); }
    finally { setDeleting(false); }
  };

  const fetchData = useCallback(async (p = 1, s = '', status = '', ownerFilter = paramOwnerId, signal?: AbortSignal) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(p), limit: '20' });
      if (s) params.set('search', s);
      if (status) params.set('status', status);
      if (ownerFilter) params.set('ownerId', ownerFilter);
      const data = await api<{ accounts: FleetItem[]; total: number }>(`/api/admin/fleet/accounts?${params}`);
      if (signal?.aborted) return;
      setAccounts(data.accounts);
      setTotal(data.total);
      setPage(p);
    } catch (err) { if (!(signal?.aborted)) console.error(err); }
    finally { if (!(signal?.aborted)) setLoading(false); }
  }, [paramOwnerId]);

  useEffect(() => {
    const ac = new AbortController();
    const initial = typeof paramSearch === 'string' ? paramSearch : '';
    if (initial) setSearch(initial);
    fetchData(1, initial, '', paramOwnerId, ac.signal);
    return () => ac.abort();
  }, [paramOwnerId, paramSearch]);

  const openEdit = async (item: FleetItem) => {
    setEditItem(item);
    setEditData({
      companyName: item.companyName,
      status: item.status, billingEmail: item.billingEmail,
      paymentTermsDays: String(item.paymentTermsDays ?? 15),
      primaryContactName: item.primaryContactName || '',
      primaryContactPhone: item.primaryContactPhone || '',
      primaryContactEmail: item.primaryContactEmail || '',
      billingAddress: item.billingAddress || '',
      billingCity: item.billingCity || '',
      billingState: item.billingState || '',
      billingZip: item.billingZip || '',
      taxId: item.taxId || '',
      pricingTierId: item.pricingTierId || '',
      invoiceAutoGeneration: item.invoiceAutoGeneration || 'off',
      autoGenFrequency: item.autoGenFrequency || 'monthly',
      autoGenDayOfWeek: item.autoGenDayOfWeek != null ? String(item.autoGenDayOfWeek) : '1',
    });
    // Fetch pricing tiers for this account's owner
    if (item.ownerId) {
      try {
        const data = await api<{ tiers: { id: string; tierName: string }[] }>(`/api/admin/fleet/tiers?ownerId=${item.ownerId}&limit=100`);
        setTiers(data.tiers);
      } catch { setTiers([]); }
    }
  };

  const handleSave = async () => {
    if (!editItem || !editData) return;
    setSaving(true);
    try {
      const body: any = { ...editData };
      const days = parseInt(body.paymentTermsDays ?? '15', 10);
      body.paymentTermsDays = Number.isFinite(days) ? Math.max(0, Math.min(365, days)) : 15;
      if (!body.pricingTierId) delete body.pricingTierId;
      // Normalize auto-gen day to int (or null when not weekly/biweekly).
      if (body.invoiceAutoGeneration && body.invoiceAutoGeneration !== 'off'
          && (body.autoGenFrequency === 'weekly' || body.autoGenFrequency === 'biweekly')) {
        const dow = parseInt(String(body.autoGenDayOfWeek ?? '1'), 10);
        body.autoGenDayOfWeek = Number.isFinite(dow) && dow >= 0 && dow <= 6 ? dow : 1;
      } else {
        body.autoGenDayOfWeek = null;
      }
      await api(`/api/admin/fleet/accounts/${editItem.id}`, { method: 'PUT', body });
      setEditItem(null);
      fetchData(page, search, statusFilter);
    } catch (err: any) { setInlineMsg({ type: 'error', message: err.message }); }
    finally { setSaving(false); }
  };

  const csvEndpoint = `/api/admin/fleet/accounts?${new URLSearchParams({ ...(search && { search }), ...(statusFilter && { status: statusFilter }) })}`;

  const renderItem = ({ item }: { item: FleetItem }) => (
    <View style={styles.row}>
      <View style={styles.rowBody}>
        <View style={styles.rowHeader}>
          <Text style={styles.name}>{item.companyName}</Text>
          <View style={[styles.badge, { backgroundColor: statusColor(item.status) + '15' }]}>
            <Text style={[styles.badgeText, { color: statusColor(item.status) }]}>{item.status}</Text>
          </View>
        </View>
        <Text style={styles.meta}>
          {item.billingEmail}
          {item.primaryContactName ? ` · ${titleCase(item.primaryContactName)}` : ''}
          {item.primaryContactPhone ? ` · ${formatPhone(item.primaryContactPhone)}` : ''}
        </Text>
        <Text style={styles.sub}>Owner: {item.businessName}</Text>
      </View>
      <TouchableOpacity onPress={() => openEdit(item)} hitSlop={8}>
        <Ionicons name="create-outline" size={18} color={colors.primary} />
      </TouchableOpacity>
      <TouchableOpacity onPress={() => setConfirmDelete(item.id)} hitSlop={8}>
        <Ionicons name="trash-outline" size={18} color={colors.danger} />
      </TouchableOpacity>
    </View>
  );

  return (
    <View style={styles.container}>
      <ContentContainer role="admin" footer={false}>
      <AdminToolbar
        search={search} onSearchChange={setSearch} onSearch={() => fetchData(1, search, statusFilter)}
        total={total} page={page} limit={20} onPageChange={(p) => fetchData(p, search, statusFilter)}
        csvEndpoint={csvEndpoint} csvFilename="fleet-accounts.csv" label="account"
        filterOptions={STATUS_FILTERS} activeFilter={statusFilter}
        onFilterChange={(v) => { setStatusFilter(v); fetchData(1, search, v); }}
      />
      {loading ? (
        <ActivityIndicator size="large" color={colors.primary} style={{ marginTop: spacing.xl }} />
      ) : (
        <FlatList ListFooterComponent={<PageFooter />} data={accounts} keyExtractor={item => item.id} renderItem={renderItem}
          ListEmptyComponent={<Text style={styles.empty}>No fleet accounts found</Text>}
        />
      )}

      <InlineConfirm
        message={`Delete fleet account "${accounts.find(a => a.id === confirmDelete)?.companyName ?? ''}"?`}
        visible={!!confirmDelete}
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete(null)}
        confirmLabel="Delete"
        loading={deleting}
        variant="danger"
      />
      <InlineMessage type={inlineMsg?.type || 'error'} message={inlineMsg?.message || ''} visible={!!inlineMsg && !editItem} onDismiss={() => setInlineMsg(null)} />

      <Modal visible={!!editItem} transparent animationType="slide" onRequestClose={() => setEditItem(null)}>
        <View style={styles.modalOverlay}>
          <View style={[styles.modalContent, { paddingBottom: modalBottomPadding }]}>
            <Text style={styles.modalTitle}>Edit Fleet Account</Text>
            {editData && (
              <ScrollView style={{ maxHeight: isMobile ? height * 0.6 : 500 }} nestedScrollEnabled>
                <Text style={styles.fieldLabel}>Company Name</Text>
                <TextInput style={styles.fieldInput} value={editData.companyName} onChangeText={(v) => setEditData({ ...editData, companyName: v })} maxLength={100} />
                <Text style={styles.fieldLabel}>Status</Text>
                <View style={styles.chipRow}>
                  {['active', 'suspended', 'cancelled'].map(s => (
                    <TouchableOpacity key={s} onPress={() => setEditData({ ...editData, status: s })}
                      style={[styles.chip, editData.status === s && styles.chipActive]}>
                      <Text style={[styles.chipText, editData.status === s && { color: colors.white }]}>{s}</Text>
                    </TouchableOpacity>
                  ))}
                </View>
                <Text style={styles.fieldLabel}>Billing Email</Text>
                <TextInput style={styles.fieldInput} value={editData.billingEmail} onChangeText={(v) => setEditData({ ...editData, billingEmail: v })} keyboardType="email-address" autoCapitalize="none" maxLength={255} />
                <Text style={styles.fieldLabel}>Net days until due</Text>
                <TextInput style={styles.fieldInput} value={editData.paymentTermsDays} onChangeText={(v) => setEditData({ ...editData, paymentTermsDays: v })} keyboardType="numeric" placeholder="15" placeholderTextColor={colors.textLight} maxLength={3} />
                <Text style={styles.fieldLabel}>Primary Contact Name</Text>
                <TextInput style={styles.fieldInput} value={editData.primaryContactName} onChangeText={(v) => setEditData({ ...editData, primaryContactName: v })} maxLength={32} />
                <Text style={styles.fieldLabel}>Primary Contact Phone</Text>
                <TextInput style={styles.fieldInput} value={editData.primaryContactPhone} onChangeText={(v) => setEditData({ ...editData, primaryContactPhone: v })} keyboardType="phone-pad" maxLength={20} />
                <Text style={styles.fieldLabel}>Primary Contact Email</Text>
                <TextInput style={styles.fieldInput} value={editData.primaryContactEmail} onChangeText={(v) => setEditData({ ...editData, primaryContactEmail: v })} keyboardType="email-address" autoCapitalize="none" maxLength={255} />
                <Text style={styles.fieldLabel}>Billing Address</Text>
                <TextInput style={styles.fieldInput} value={editData.billingAddress} onChangeText={(v) => setEditData({ ...editData, billingAddress: v })} maxLength={100} />
                <Text style={styles.fieldLabel}>Billing City</Text>
                <TextInput style={styles.fieldInput} value={editData.billingCity} onChangeText={(v) => setEditData({ ...editData, billingCity: v })} maxLength={32} />
                <Text style={styles.fieldLabel}>Billing State</Text>
                <TextInput style={styles.fieldInput} value={editData.billingState} onChangeText={(v) => setEditData({ ...editData, billingState: v })} autoCapitalize="characters" maxLength={2} />
                <Text style={styles.fieldLabel}>Billing Zip</Text>
                <TextInput style={styles.fieldInput} value={editData.billingZip} onChangeText={(v) => setEditData({ ...editData, billingZip: v })} keyboardType="number-pad" maxLength={10} />
                <Text style={styles.fieldLabel}>Tax ID</Text>
                <TextInput style={styles.fieldInput} value={editData.taxId} onChangeText={(v) => setEditData({ ...editData, taxId: v })} maxLength={20} />
                {tiers.length > 0 && (
                  <>
                    <Text style={styles.fieldLabel}>Pricing Tier</Text>
                    <View style={styles.chipRow}>
                      <TouchableOpacity onPress={() => setEditData({ ...editData, pricingTierId: '' })}
                        style={[styles.chip, !editData.pricingTierId && styles.chipActive]}>
                        <Text style={[styles.chipText, !editData.pricingTierId && { color: colors.white }]}>None</Text>
                      </TouchableOpacity>
                      {tiers.map(t => (
                        <TouchableOpacity key={t.id} onPress={() => setEditData({ ...editData, pricingTierId: t.id })}
                          style={[styles.chip, editData.pricingTierId === t.id && styles.chipActive]}>
                          <Text style={[styles.chipText, editData.pricingTierId === t.id && { color: colors.white }]}>{t.tierName}</Text>
                        </TouchableOpacity>
                      ))}
                    </View>
                  </>
                )}
                <Text style={styles.fieldLabel}>Auto Invoicing</Text>
                <View style={styles.chipRow}>
                  {[['off', 'Off'], ['generate_notify', 'Generate & Notify'], ['generate_send', 'Generate & Auto-Send']].map(([v, label]) => (
                    <TouchableOpacity key={v} onPress={() => setEditData({ ...editData, invoiceAutoGeneration: v })}
                      style={[styles.chip, editData.invoiceAutoGeneration === v && styles.chipActive]}>
                      <Text style={[styles.chipText, editData.invoiceAutoGeneration === v && { color: colors.white }]}>{label}</Text>
                    </TouchableOpacity>
                  ))}
                </View>
                {editData.invoiceAutoGeneration && editData.invoiceAutoGeneration !== 'off' && (
                  <>
                    <Text style={styles.fieldLabel}>Frequency</Text>
                    <View style={styles.chipRow}>
                      {[['weekly', 'Weekly'], ['biweekly', 'Biweekly'], ['monthly', 'Monthly']].map(([v, label]) => (
                        <TouchableOpacity key={v} onPress={() => setEditData({ ...editData, autoGenFrequency: v })}
                          style={[styles.chip, editData.autoGenFrequency === v && styles.chipActive]}>
                          <Text style={[styles.chipText, editData.autoGenFrequency === v && { color: colors.white }]}>{label}</Text>
                        </TouchableOpacity>
                      ))}
                    </View>
                    {(editData.autoGenFrequency === 'weekly' || editData.autoGenFrequency === 'biweekly') && (
                      <>
                        <Text style={styles.fieldLabel}>Run Day</Text>
                        <View style={styles.chipRow}>
                          {[['0','Sun'],['1','Mon'],['2','Tue'],['3','Wed'],['4','Thu'],['5','Fri'],['6','Sat']].map(([v, label]) => (
                            <TouchableOpacity key={v} onPress={() => setEditData({ ...editData, autoGenDayOfWeek: v })}
                              style={[styles.chip, String(editData.autoGenDayOfWeek) === v && styles.chipActive]}>
                              <Text style={[styles.chipText, String(editData.autoGenDayOfWeek) === v && { color: colors.white }]}>{label}</Text>
                            </TouchableOpacity>
                          ))}
                        </View>
                      </>
                    )}
                  </>
                )}
              </ScrollView>
            )}
            <InlineMessage type={inlineMsg?.type || 'error'} message={inlineMsg?.message || ''} visible={!!inlineMsg} onDismiss={() => setInlineMsg(null)} />
            <View style={styles.modalActions}>
              <TouchableOpacity style={styles.cancelBtn} onPress={() => setEditItem(null)}>
                <Text style={styles.cancelText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.saveBtn} onPress={handleSave} disabled={saving}>
                <Text style={styles.saveText}>{saving ? 'Saving...' : 'Save'}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
      </ContentContainer>
    </View>
  );
}

const createStyles = (c: ColorTheme, isMobile: boolean) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background, padding: spacing.md },
  row: { flexDirection: 'row', alignItems: 'center', paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: c.borderLight, gap: 8 },
  rowBody: { flex: 1 },
  rowHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  name: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: c.text },
  badge: { paddingHorizontal: 8, paddingVertical: 2, borderRadius: 8 },
  badgeText: { fontSize: fontSize.xs, fontWeight: fontWeight.semibold },
  meta: { fontSize: fontSize.xs, color: c.textSecondary, marginTop: 2 },
  sub: { fontSize: fontSize.xs, color: c.textLight, marginTop: 2 },
  empty: { textAlign: 'center', color: c.textSecondary, marginTop: spacing.xl, fontSize: fontSize.md },
  modalOverlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: isMobile ? 'flex-end' : 'center', alignItems: 'center', padding: isMobile ? 0 : spacing.lg },
  modalContent: { backgroundColor: c.surface, borderRadius: isMobile ? 0 : borderRadius.lg, borderTopLeftRadius: borderRadius.lg, borderTopRightRadius: borderRadius.lg, padding: spacing.lg, width: '100%', maxWidth: isMobile ? undefined : 420 },
  modalTitle: { fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: c.text, marginBottom: spacing.md },
  fieldLabel: { fontSize: fontSize.xs, fontWeight: fontWeight.semibold, color: c.textSecondary, marginTop: spacing.sm, textTransform: 'uppercase' },
  fieldInput: { backgroundColor: c.surfaceSecondary, borderRadius: borderRadius.sm, padding: spacing.sm, fontSize: fontSize.sm, color: c.text, marginTop: 4, borderWidth: 1, borderColor: c.border },
  chipRow: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs, marginTop: spacing.xs },
  chip: { paddingHorizontal: 12, paddingVertical: 6, borderRadius: 16, backgroundColor: c.surfaceSecondary, borderWidth: 1, borderColor: c.border },
  chipActive: { backgroundColor: c.primary, borderColor: c.primary },
  chipText: { fontSize: fontSize.xs, color: c.textSecondary, fontWeight: fontWeight.medium, textTransform: 'capitalize' },
  modalActions: { flexDirection: 'row', justifyContent: 'flex-end', gap: spacing.sm, marginTop: spacing.md },
  cancelBtn: { paddingHorizontal: spacing.md, paddingVertical: spacing.sm, borderRadius: borderRadius.sm },
  cancelText: { fontSize: fontSize.sm, color: c.textSecondary, fontWeight: fontWeight.medium },
  saveBtn: { paddingHorizontal: spacing.md, paddingVertical: spacing.sm, borderRadius: borderRadius.sm, backgroundColor: c.primary },
  saveText: { fontSize: fontSize.sm, color: c.white, fontWeight: fontWeight.semibold },
});
const useStyles = (c: ColorTheme, isMobile: boolean) => React.useMemo(() => createStyles(c, isMobile), [c, isMobile]);
